import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import api from '../../services/api';
import { StatusBadge, LoadingSpinner } from '../../components/UI';
import { format } from 'date-fns';

const TABS = [
  { key: 'pending',   label: '⏳ Pending'    },
  { key: 'approved',  label: '✅ Approved'   },
  { key: 'rejected',  label: '❌ Rejected'   },
  { key: 'objection', label: '⚠️ Objection'  },
  { key: 'all',       label: '📋 All History'},
];

export default function DeptApplications() {
  const { role } = useSelector(s => s.auth);
  const [apps, setApps]       = useState([]);
  const [counts, setCounts]   = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('pending');
  const [nocTypes, setNocTypes] = useState([]);
  const [filters, setFilters] = useState({ noc_type_id: '', date_from: '', date_to: '' });

  const load = (activeTab = tab) => {
    setLoading(true);
    const p = new URLSearchParams({
      tab: activeTab,
      ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
    });
    api.get(`/dept/applications?${p}`)
      .then(r => setApps(r.applications || []))
      .catch(() => setApps([]))
      .finally(() => setLoading(false));
  };

  // Load counts for each tab
  const loadCounts = () => {
    TABS.forEach(t => {
      api.get(`/dept/applications?tab=${t.key}`)
        .then(r => setCounts(prev => ({ ...prev, [t.key]: r.applications?.length || 0 })))
        .catch(() => {});
    });
  };

  useEffect(() => {
    api.get('/noc-types/public?active_only=1').then(r => setNocTypes(r.noc_types || []));
    load();
    loadCounts();
  }, []);

  const switchTab = (key) => {
    setTab(key);
    load(key);
  };

  const isFinalDept = role === 'final_dept';

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Applications</h1>
          <p className="text-sm text-gray-500">
            {isFinalDept ? 'Final approval queue' : 'Review and process NOC applications'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-xl overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => switchTab(t.key)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all
              ${tab === t.key ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
            {counts[t.key] > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold
                ${t.key === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  t.key === 'approved' ? 'bg-green-100 text-green-700' :
                  t.key === 'rejected' ? 'bg-red-100 text-red-700' :
                  t.key === 'objection' ? 'bg-orange-100 text-orange-700' :
                  'bg-blue-100 text-blue-700'}`}>
                {counts[t.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="card mb-4 py-3">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <select className="form-select text-sm" value={filters.noc_type_id}
            onChange={e => setFilters(f => ({ ...f, noc_type_id: e.target.value }))}>
            <option value="">All NOC Types</option>
            {nocTypes.map(n => <option key={n.id} value={n.id}>{n.noc_name}</option>)}
          </select>
          <input type="date" className="form-input text-sm" value={filters.date_from}
            onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} />
          <input type="date" className="form-input text-sm" value={filters.date_to}
            onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} />
          <button onClick={() => load()} className="btn-primary text-sm">🔍 Search</button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner size="lg" className="py-20" />
      ) : apps.length === 0 ? (
        <div className="card text-center py-16">
          <p className="text-5xl mb-3">📂</p>
          <p className="text-lg font-semibold text-gray-700">
            {tab === 'pending'
              ? isFinalDept
                ? 'No applications pending for final approval'
                : 'No pending applications'
              : `No ${tab} applications found`}
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {tab === 'pending' && isFinalDept
              ? 'Applications will appear here after related department approves them'
              : 'Try switching tabs or clearing filters'}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-500">
            {apps.length} application(s) found
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>App No</th>
                  <th>NOC Type</th>
                  <th>Applicant</th>
                  <th>Mobile</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {apps.map(app => (
                  <tr key={app.id}>
                    <td className="font-mono text-xs text-blue-700 font-semibold">
                      {app.application_no}
                    </td>
                    <td className="text-sm font-medium">{app.noc_name}</td>
                    <td className="text-sm">{app.user_name}</td>
                    <td className="text-xs text-gray-500">{app.mobile}</td>
                    <td><StatusBadge status={app.status} /></td>
                    <td className="text-xs text-gray-400">
                      {app.submitted_at
                        ? format(new Date(app.submitted_at), 'dd/MM/yyyy')
                        : '—'}
                    </td>
                    <td>
                      <Link to={`/dept/applications/${app.id}`}
                        className="text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 px-3 py-1.5 rounded-full">
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}